import { useCallback, useEffect, useRef } from 'react';

interface UseDatePickerKeyboardOptions {
  isPickerOpen: boolean;
  onClose: () => void;
}

/**
 * Custom hook to handle keyboard navigation for date pickers
 */
export function useDatePickerKeyboard({
  isPickerOpen,
  onClose,
}: UseDatePickerKeyboardOptions): void {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const handleKeyDown = useCallback((event: Event): void => {
    if (!(event instanceof KeyboardEvent)) return;

    const { key } = event;

    if (key === 'Enter' || key === ' ') {
      const isAtMinuteSelection = (): boolean => {
        const { activeElement } = document;
        if (!activeElement) return false;

        const isMinuteFocused =
          activeElement.closest('[aria-label*="minute" i]') !== null;
        return isMinuteFocused;
      };

      if (isAtMinuteSelection()) {
        event.preventDefault();
        onCloseRef.current();
      }
    }

    if (key === 'Escape') {
      event.preventDefault();
      onCloseRef.current();
    }
  }, []);

  useEffect(() => {
    if (!isPickerOpen) return undefined;

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPickerOpen, handleKeyDown]);
}
